/**
 * Dev-only guard against silent list truncation.
 *
 * Several screens used to resolve foreign-key labels by fetching a capped
 * tenant-wide list (`limit: 200`/`500`/…) and joining by id on the client.
 * When the tenant had more rows than the cap, the map silently omitted the
 * overflow and the affected cells rendered a placeholder ("…"/"—") that
 * looked like real data — confirmed on a 323-user tenant.
 *
 * The fix is to resolve labels server-side or fetch exactly the referenced
 * ids (`findByIds`). This helper is the backstop: whenever code still builds
 * a lookup from a list response, call it so a capped page screams in the dev
 * console instead of failing silently in production. No-op outside dev.
 *
 * De-duplicates by message so it can be called inline in a component body
 * (it fires once per distinct truncation, not once per render).
 */
const warnedMessages = new Set<string>();

export function warnIfTruncated(
  result: { data: readonly unknown[]; meta?: { total: number } } | undefined,
  context: string
): void {
  if (!import.meta.env.DEV || !result?.meta) return;
  const received = result.data.length;
  if (result.meta.total > received) {
    const message =
      `[fk-label] ${context}: received ${received} of ${result.meta.total} rows — this list is ` +
      `capped. A client-side id→label map built from it will silently miss the rest. Resolve ` +
      `labels server-side or fetch by id (findByIds).`;
    if (warnedMessages.has(message)) return;
    warnedMessages.add(message);
    console.warn(message);
  }
}
